/**
 * Reaction studio UI: consent sheet, the draggable self-view bubble (with the REC indicator),
 * the review sheet (preview → Publish / Discard), take face bubbles and the take player.
 */
import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as RPointerEvent } from 'react';
import type { Take } from '@clutch/core';
import { recClock } from './marks';
import type { ReactCam, Recording } from './recorder';
import { initials } from '../room/derive';
import { useClient } from '../client';
import { loadFan } from '../fan';

export const CONSENT_KEY = 'clutch.cam.consent';

// ───────────── Consent ─────────────

export function CamConsent({ quick, onAllow, onCancel }: { quick: boolean; onAllow: () => void; onCancel: () => void }) {
  return (
    <div className="sheet-scrim" onClick={onCancel}>
      <section className="sheet consent" role="dialog" aria-modal="true" aria-label="React cam" data-testid="cam-consent" onClick={(e) => e.stopPropagation()}>
        <span className="sheet__grab" aria-hidden="true" />
        <div className="consent__art" aria-hidden="true">
          <span className="consent__ring" />
          <span className="consent__face">🫣</span>
          <span className="consent__rec">● REC</span>
        </div>
        <h2 className="sheet__title">{quick ? 'Record a 10-second take' : 'Turn on your React cam'}</h2>
        <ul className="consent__list">
          <li>
            <b>Only while REC shows.</b> Your camera records only when the red REC light is on.
          </li>
          <li>
            <b>Nothing posts by itself.</b> You watch it back, then Publish or Discard.
          </li>
          <li>
            <b>Clutch cuts your reel.</b> We line your reaction up with the plays that swung the game.
          </li>
        </ul>
        <button className="btn btn--primary btn--block" onClick={onAllow} data-testid="cam-allow">
          {quick ? 'Allow camera & record' : 'Allow camera'}
        </button>
        <button className="btn btn--ghost btn--block" onClick={onCancel}>
          Not now
        </button>
      </section>
    </div>
  );
}

// ───────────── Self-view bubble ─────────────

const BUBBLE = 104;

export function CamBubble({ cam, onRecord, onClose }: { cam: ReactCam; onRecord: () => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number }>(() => ({ x: 14, y: 132 })); // from the right / top
  const drag = useRef<{ px: number; py: number; x: number; y: number; moved: boolean }>();

  useEffect(() => {
    if (video.current && cam.stream && video.current.srcObject !== cam.stream) {
      video.current.srcObject = cam.stream;
      void video.current.play().catch(() => undefined);
    }
  }, [cam.stream]);

  const recording = cam.phase === 'recording';
  const progress = recording ? Math.min(1, cam.elapsed / cam.limitMs) : 0;

  const down = (e: RPointerEvent) => {
    drag.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const move = (e: RPointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    if (Math.abs(dx) + Math.abs(dy) > 6) d.moved = true;
    if (d.moved) setPos({ x: Math.max(8, d.x - dx), y: Math.max(60, Math.min(window.innerHeight - BUBBLE - 120, d.y + dy)) });
  };
  const up = () => {
    const d = drag.current;
    drag.current = undefined;
    if (!d?.moved) return;
    // Pin to the nearest side.
    const w = Math.min(window.innerWidth, 480);
    setPos((p) => ({ ...p, x: p.x > w / 2 - BUBBLE / 2 ? w - BUBBLE - 14 : 14 }));
  };

  return (
    <div
      className={`cambubble${recording ? ' is-rec' : ''}`}
      style={{ right: pos.x, top: pos.y, '--p': progress } as CSSProperties}
      data-testid="cam-bubble"
    >
      <div className="cambubble__face" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        <video ref={video} muted playsInline autoPlay aria-label="Your camera" />
        <svg className="cambubble__ring" viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="50" r="47" pathLength="1" />
        </svg>
      </div>
      {recording ? (
        <span className="rec" data-testid="rec-indicator" role="status" aria-live="polite">
          <span className="rec__dot" />
          REC {recClock(cam.elapsed)}
          {cam.markCount > 0 && <i>· {cam.markCount} plays</i>}
        </span>
      ) : (
        <button className="cambubble__x" onClick={onClose} aria-label="Turn off React cam">
          ✕
        </button>
      )}
      <button
        className={`cambubble__rec${recording ? ' is-on' : ''}`}
        onClick={recording ? cam.stop : onRecord}
        aria-label={recording ? 'Stop recording' : 'Start recording'}
        data-testid="cam-rec"
      >
        <span />
      </button>
    </div>
  );
}

// ───────────── Review ─────────────

export function TakeReview({
  rec,
  playsCovered,
  busy,
  error,
  onPublish,
  onDiscard,
}: {
  rec: Recording;
  playsCovered: number;
  busy: boolean;
  error?: string;
  onPublish: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="sheet-scrim">
      <section className="sheet review" role="dialog" aria-modal="true" aria-label="Your take" data-testid="take-review">
        <span className="sheet__grab" aria-hidden="true" />
        <div className="review__video">
          <video src={rec.url} playsInline autoPlay loop muted controls data-testid="take-preview" />
          <span className="review__len">{recClock(rec.durationMs)}</span>
        </div>
        <h2 className="sheet__title">Your take</h2>
        <p className="review__sub">
          {playsCovered > 0 ? (
            <>
              Covers <b>{playsCovered}</b> play{playsCovered === 1 ? '' : 's'} — friends see it on those plays.
            </>
          ) : (
            'No plays happened while you recorded. It posts to the room anyway.'
          )}
        </p>
        {error && <p className="alert">{error}</p>}
        <p className="review__privacy">Publishing makes your recording visible to people in this room and anyone with its link. Share only your own content. You can remove it from My takes.</p>
        <div className="review__actions">
          <button className="btn btn--ghost" onClick={onDiscard} disabled={busy} data-testid="take-discard">
            Discard
          </button>
          <button className="btn btn--primary" onClick={onPublish} disabled={busy} data-testid="take-publish">
            {busy ? 'Posting…' : 'Publish'}
          </button>
        </div>
      </section>
    </div>
  );
}

// ───────────── Faces ─────────────

/** A frame of a take at `offsetMs`, in a circle. Falls back to initials until a frame is ready. */
export function FaceThumb({ url, offsetMs, name, size = 34 }: { url: string; offsetMs?: number; name: string; size?: number }) {
  const v = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const el = v.current;
    if (!el) return;
    const on = () => {
      try {
        if (offsetMs !== undefined && Number.isFinite(el.duration)) el.currentTime = Math.min(el.duration, offsetMs / 1000);
      } catch {
        /* first frame is fine */
      }
      setReady(true);
    };
    el.addEventListener('loadeddata', on);
    return () => el.removeEventListener('loadeddata', on);
  }, [url, offsetMs]);
  return (
    <span className={`face${ready ? ' is-ready' : ''}`} style={{ '--size': `${size}px` } as CSSProperties}>
      <b>{initials(name)}</b>
      <video ref={v} src={url} muted playsInline preload="auto" aria-hidden="true" />
    </span>
  );
}

export function TakeBubbles({
  takes,
  seq,
  urlOf,
  onOpen,
  max = 3,
}: {
  takes: Take[];
  seq: number;
  urlOf: (t: Take) => string;
  onOpen: (t: Take, offsetMs?: number) => void;
  max?: number;
}) {
  if (!takes.length) return null;
  return (
    <span className="takebubbles" aria-label={`${takes.length} take${takes.length === 1 ? '' : 's'} on this play`}>
      {takes.slice(0, max).map((t) => {
        const off = t.marks.find((m) => m.seq === seq)?.offsetMs;
        return (
          <button
            key={t.takeId}
            className="takebubble"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(t, off);
            }}
            aria-label={`Play ${t.displayName}’s take`}
            data-testid="take-bubble"
          >
            <FaceThumb url={urlOf(t)} offsetMs={off} name={t.displayName} />
            <span className="takebubble__play" aria-hidden="true">
              ▶
            </span>
          </button>
        );
      })}
      {takes.length > max && <span className="takebubbles__more">+{takes.length - max}</span>}
    </span>
  );
}

export function TakePlayer({ take, url, offsetMs, onClose, onDeleted }: { take: Take; url: string; offsetMs?: number; onClose: () => void; onDeleted?: () => void }) {
  const client = useClient();
  const mine = loadFan(client.mode)?.fanId === take.fanId;
  const v = useRef<HTMLVideoElement>(null);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [reported, setReported] = useState(false);
  const [reason, setReason] = useState('abuse');
  const [reportOpen, setReportOpen] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const [shared, setShared] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canReport, setCanReport] = useState(false);
  const [shareError, setShareError] = useState(false);
  const link = `${location.origin}/room/${encodeURIComponent(take.roomId)}?take=${encodeURIComponent(take.takeId)}`;
  useEffect(() => {
    let alive = true;
    client.session().then((session) => alive && setCanReport(session.required)).catch(() => undefined);
    return () => { alive = false; };
  }, [client]);
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setShareError(false); }
    catch { setShareError(true); }
  };
  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: `${take.displayName}’s Clutch take`, url: link });
      else await navigator.clipboard.writeText(link);
      setShared(true);
      setShareError(false);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') setShareError(true);
    }
  };
  useEffect(() => {
    const el = v.current;
    if (!el) return;
    const start = Math.max(0, ((offsetMs ?? 0) - 2500) / 1000);
    const on = () => {
      try {
        el.currentTime = start;
      } catch {
        /* play from the top */
      }
      void el.play().catch(() => undefined);
    };
    el.addEventListener('loadedmetadata', on, { once: true });
    return () => el.removeEventListener('loadedmetadata', on);
  }, [url, offsetMs]);
  return (
    <div className="sheet-scrim" onClick={onClose}>
      <section className="sheet takeplayer" role="dialog" aria-modal="true" aria-label={`${take.displayName}’s take`} data-testid="take-player" onClick={(e) => e.stopPropagation()}>
        <span className="sheet__grab" aria-hidden="true" />
        <div className="takeplayer__video">
          <video ref={v} src={url} playsInline controls />
        </div>
        <p className="takeplayer__who">
          <span className="avatar avatar--sm">{initials(take.displayName)}</span>
          <b>{take.displayName}</b>
          <span className="muted">· {recClock(take.durationMs)} · {take.startedAtSeq < 0 ? 'Pregame take' : `${take.marks.length} plays`}</span>
        </p>
        {!take.marks.length && <p className="muted">Your original recording is saved. A Clutch Reel becomes available when a recording captures game plays.</p>}
        <div className="takeplayer__actions"><a className="btn btn--primary" href={url} download={`clutch-${take.takeId}.${take.mimeType.includes('mp4') ? 'mp4' : 'webm'}`}>Download video</a><button className="btn btn--ghost" onClick={share}>{shared ? 'Shared ✓' : 'Share take'}</button></div>
        <button className="text-action" onClick={copyLink}>{copied ? 'Link copied ✓' : 'Copy link'}</button>
        {shareError && <p role="alert">Couldn’t share the link. You can download the video instead.</p>}
        {mine ? <div className="takeplayer__manage">{confirmRemove ? <>
          <p>Delete this recording from Clutch? Download a copy first if you want to keep it.</p>
          <button className="btn btn--ghost" disabled={removing} onClick={async () => {
            setRemoving(true); setActionError(undefined);
            try { await client.deleteTake(take); onDeleted?.(); onClose(); }
            catch { setActionError('Couldn’t delete your recording. Try again.'); }
            finally { setRemoving(false); }
          }}>{removing ? 'Deleting…' : 'Delete recording'}</button><button className="btn btn--ghost" onClick={() => setConfirmRemove(false)}>Keep it</button>
        </> : <button className="text-action" onClick={() => setConfirmRemove(true)}>Delete my take</button>}</div>
          : canReport ? <div className="takeplayer__manage">{reported ? <p role="status">Report sent. A moderator will review this take.</p> : reportOpen ? <>
            <label>Report reason <select value={reason} onChange={(e) => setReason(e.target.value)}><option value="abuse">Abuse or harmful content</option><option value="privacy">Privacy or consent</option><option value="spam">Spam</option><option value="other">Other concern</option></select></label>
            <button className="btn btn--ghost" disabled={removing} onClick={async () => {
              setRemoving(true); setActionError(undefined);
              try { await client.reportTake(take, reason); setReported(true); } catch { setActionError('Couldn’t send your report. Please try again.'); }
              finally { setRemoving(false); }
            }}>Send report</button></> : <button className="text-action" onClick={() => setReportOpen(true)}>Report take</button>}</div> : null}
        {actionError && <p role="alert">{actionError}</p>}
        <button className="btn btn--ghost btn--block" onClick={onClose}>
          Close
        </button>
      </section>
    </div>
  );
}
